<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" @click="close()"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-4xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Manage Cards
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to add a credit card.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors"/>
                                    <SlideoutFlashMessage ref="create_regions_slideout_flash"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <h2 class="text-xl text-bold">{{ form.title }}</h2>
                                                <div class="grid grid-cols-2 gap-4">
                                                    <div class="col-span-2">
                                                        <label for="card-name"
                                                               class="block text-sm text-gray-900 required">
                                                            Card Reference
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="card-name" v-model="form.name"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.name}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-span-2">
                                                        <label for="card-number"
                                                               class="block text-sm text-gray-900 required">
                                                            Card Number (Last 4 Digits)
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="card-number" minlength="4" maxlength="4"
                                                                   v-model="form.card_number"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.card_number}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="expiry-month"
                                                               class="block text-sm text-gray-900 required">
                                                            Expiry Month
                                                        </label>
                                                        <div class="mt-1">
                                                            <select v-model="form.expiry_month"
                                                                    :class="{'border-red-500': form.errors.expiry_month}"
                                                                    class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected
                                                                        v-html="'Select an expiry month'"></option>
                                                                <option v-for="(expiry_month, index) in expiry_months"
                                                                        :value="expiry_month"
                                                                        v-html="expiry_month"></option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label for="expiry-year"
                                                               class="block text-sm text-gray-900 required">
                                                            Expiry Year
                                                        </label>
                                                        <div class="mt-1">
                                                            <select v-model="form.expiry_year"
                                                                    :class="{'border-red-500': form.errors.expiry_year}"
                                                                    class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                                <option value selected
                                                                        v-html="'Select an expiry year'"></option>
                                                                <option v-for="(expiry_year, index) in expiry_years"
                                                                        :value="expiry_year"
                                                                        v-html="expiry_year"></option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div class="flex items-center" v-if="can_access_admin === true">
                                                        <label for="group-card"
                                                               class="block text-sm text-gray-900">
                                                            Group Card?
                                                        </label>
                                                        <div class="ml-3">
                                                            <input type="checkbox" id="group-card"
                                                                   v-model="form.is_group_card"
                                                                   class="focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"
                                                                   :class="{'border-red-500': form.errors.is_group_card}"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div class="justify-end"
                                                         :class="{'col-span-2': can_access_admin === false}">
                                                        <button :disabled="form.processing || !form.isDirty"
                                                                type="submit"
                                                                class="float-right inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                            <span v-if="loading === true">Saving Card..</span>
                                                            <span v-else>{{ form.button_title }}</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div class="rounded-md bg-blue-50 p-4 mt-2">
                                                    <div class="flex">
                                                        <div class="flex-shrink-0">
                                                            <InformationCircleIcon class="h-5 w-5 text-blue-400"
                                                                                   aria-hidden="true"/>
                                                        </div>
                                                        <div class="ml-3 flex-1 md:flex md:justify-between">
                                                            <p class="text-sm text-blue-700">
                                                                Selecting Group Card will make the card available to ALL team travel managers and users.<br/>
                                                                Only use this option if the card is intended for multiple use.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <h2 class="text-xl text-bold">Cards</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-9 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Reference
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Card Number
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Expiry
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Group Card
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Delete</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="cards.length > 0" v-for="(card, index) in cards"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ card.name }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ card.card_number }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ card.expiry_month }}/{{ card.expiry_year }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-grey-900 text-sm">
                                                                <svg
                                                                    v-if="card.is_group_card"
                                                                    class="my-0 mx-auto"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    height="1em"
                                                                    viewBox="0 0 448 512">
                                                                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                                                                </svg>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                                <a @click="updateCard(card)"
                                                                class="text-grey-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                        viewBox="0 0 24 24" class="w-4 h-4 mr-3"
                                                                        stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                            stroke-linejoin="round" stroke-width="2"
                                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                                <a @click="deleteCard(card)"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                        <tr v-else>
                                                            <td colspan="2"
                                                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                                                :class="{'hidden':loading === true}">
                                                                There are no cards added for this country.
                                                            </td>
                                                        </tr>
                                                        <tr v-if="loading === true && creating === true"
                                                            :class="{'animate-pulse':loading === true}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, InformationCircleIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3';
import SlideOutErrors from "@/Components/SlideOutErrors";
import SlideoutFlashMessage from "@/Components/SlideoutFlashMessage";
import {emitter} from "@/eventHub";
import {computed} from "vue";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        SlideoutFlashMessage,
        XIcon,
        InformationCircleIcon
    },
    setup(prop, context) {

        const cards = ref([]);

        const loading = ref(null);
        const creating = ref(null);

        const show = ref(false)
        const showErrors = ref(false)

        const expiry_months = [
            '01',
            '02',
            '03',
            '04',
            '05',
            '06',
            '07',
            '08',
            '09',
            '10',
            '11',
            '12'
        ]

        const currentYear = new Date().getFullYear();
        const expiry_years = Array.from({ length: 11 }, (_, i) => String(currentYear + i));

        const form = useForm({
            id: null,
            name: null,
            card_number: null,
            expiry_month: "",
            expiry_year: "",
            is_group_card: false,
            title: 'Add New Card',
            button_title: 'Add Card',
            new: true
        })

        const can_access_admin = computed(() => usePage().props.auth.can_access_admin)


        function submit() {
            this.loading = true;
            this.creating = true;
            if(this.form.new) {
                this.form.post(route('frontend.managecards.store'), {
                    onSuccess: (page) => {
                        this.form.reset();
                        // Make a request for a cards to be fetched.
                        axios.get(route('frontend.managecards.list'))
                            .then(response => {
                                this.cards = response.data;
                            }).finally(() => {
                            this.loading = false
                            this.creating = false;
                        });
                    },
                    onError: (page) => {
                        this.loading = false;
                        this.creating = false;
                        this.showErrors = true;

                    }
                });
            } else {
                this.form.post(route('frontend.managecards.card.update', this.form.id), {
                    onSuccess: (page) => {
                        this.form.reset();
                        // Make a request for a cards to be fetched.
                        axios.get(route('frontend.managecards.list'))
                            .then(response => {
                                this.cards = response.data;
                            }).finally(() => {
                            this.loading = false
                            this.creating = false;
                        });
                    },
                    onError: (page) => {
                        this.loading = false;
                        this.creating = false;
                        this.showErrors = true;

                    }
                });
            }
        }

        function open() {
            this.form.clearErrors();
            this.getCardsList()
            this.show = true;
        }


        return {
            show,
            form,
            submit,
            cards,
            loading,
            creating,
            open,
            showErrors,
            expiry_months,
            expiry_years,
            can_access_admin
        }
    },
    methods: {
        close() {
            this.show = false;
            this.form.reset();
            this.form.clearErrors();
            this.$refs.create_regions_slideout_flash.show = false;
            this.$page.props.slideout_flash.success = null;
            this.$page.props.slideout_flash.error = null;
        },
        deleteCard(card) {

            this.$swal.fire({
                title: 'Remove Card',
                html: "You are removing a card. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        // Delete the region
                        this.$inertia.delete(route('frontend.managecards.card.delete', [card]), {
                            preserveScroll: true,
                            onSuccess: (page) => {
                                // Make a request for a regions to be fetched.
                                axios.get(route('frontend.managecards.list'))
                                    .then(response => {
                                        this.cards = response.data;
                                    }).finally(() => {
                                    this.loading = false
                                    this.creating = false;
                                });
                            },
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true;

                            }
                        });
                    }
                });
        },
        updateCard(card) {
            this.form.name = card.name;
            this.form.card_number = card.card_number;
            this.form.expiry_month = card.expiry_month;
            this.form.expiry_year = card.expiry_year;
            this.form.is_group_card = card.is_group_card > 0 ? true : false;
            this.form.title = 'Update Card';
            this.form.button_title = 'Update Card';
            this.form.new = false;
            this.form.id = card.id;
        },
        getCardsList() {
            axios.get(route('frontend.managecards.list'))
                .then(response => {
                    this.cards = response.data;
                }).finally(() => {
                this.loading = false
                this.creating = false;
            });
        }
    },
    mounted() {
        emitter.on('openManageCards', (folder_id) => {
            this.open();
        });
    }
}
</script>
